import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api, { normalizeImageUrl } from "@/lib/api";
import { sizedImage } from "@/lib/imageUtils";

const pageToGalleryKey = {
  "homepage": "homepage_gallery",
  "negozio": "shop_gallery",
  "eventi": "events_gallery",
  "didattica": "education_gallery",
  "ristorante-agricolo": "farmhouse_gallery",
  "soggiorni": "stays_gallery",
  "chi-siamo": "about_gallery"
};

const PhotoCarousel = ({ page = "homepage" }) => {
  const [galleryData, setGalleryData] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);
  const autoScrollRef = useRef(null);

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const res = await api.get("/content");
        const galleryKey = pageToGalleryKey[page] || "homepage_gallery";
        setGalleryData(res.data?.[galleryKey] || null);
      } catch (err) {
        console.log("Error loading gallery");
      }
    };
    loadGallery();
  }, [page]);

  // Auto-scroll effect
  useEffect(() => {
    if (!scrollRef.current || isPaused) {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
      return;
    }

    autoScrollRef.current = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        // Reset to start when reaching end
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollRef.current.scrollLeft = 0;
        } else {
          scrollRef.current.scrollLeft += 1;
        }
      }
    }, 20);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isPaused, galleryData]);

  const photos = galleryData?.images || [];
  
  // Don't render if disabled or no photos
  if (!galleryData?.enabled || photos.length === 0) return null;

  const title = galleryData.title || "Galleria Fotografica";
  
  // Duplicate photos for infinite scroll effect
  const duplicatedPhotos = [...photos, ...photos];

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 md:py-16 bg-[#2C2C2C] overflow-hidden">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8">
        <div className="text-center">
          <h2 
            className="text-2xl md:text-3xl font-bold text-white mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h2>
        </div>
      </div>

      {/* Marquee Container */}
      <div 
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Navigation Arrows */}
        <button
          onClick={scrollLeft}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="Scorri a sinistra"
        >
          <ChevronLeft className="w-5 h-5 text-[#2C2C2C]" />
        </button>
        <button
          onClick={scrollRight}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="Scorri a destra"
        >
          <ChevronRight className="w-5 h-5 text-[#2C2C2C]" />
        </button>

        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-[#2C2C2C] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-[#2C2C2C] to-transparent z-10 pointer-events-none" />
        
        {/* Scrolling Photos */}
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide px-4"
          style={{ scrollBehavior: 'auto' }}
        >
          {duplicatedPhotos.map((photo, index) => (
            <div
              key={`${photo.id}-${index}`}
              className="flex-shrink-0 w-[200px] h-[200px] md:w-[280px] md:h-[280px] rounded-2xl overflow-hidden border-2 border-white/10 hover:border-white/30 transition-all hover:scale-105 cursor-pointer relative"
            >
              <img loading="lazy" decoding="async"
                src={sizedImage(normalizeImageUrl(photo.url), 800)}
                alt={photo.caption || `Foto ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {photo.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <p className="text-white text-sm truncate">{photo.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PhotoCarousel;
